import React, { useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  X,
  Loader2,
  Clock,
  ChevronDown,
  ChevronRight,
  Terminal,
  Cpu,
  Activity,
} from "lucide-react";

// Note: ChevronDown and ChevronRight kept for ThinkingDisplay component
import type { AgentState, ToolState } from "@/types/assistant";

// ─────────────────────────────────────────────────────────────────────────────
// Color Classes (static strings for Tailwind to detect)
// ─────────────────────────────────────────────────────────────────────────────

interface ColorClasses {
  bg: string;
  bgLight: string;
  border: string;
  borderActive: string;
  text: string;
  textMuted: string;
}

const COLOR_CLASSES: Record<string, ColorClasses> = {
  emerald: {
    bg: "bg-emerald-500/20",
    bgLight: "bg-emerald-500/10",
    border: "border-emerald-500/30",
    borderActive: "border-emerald-500/40",
    text: "text-emerald-300",
    textMuted: "text-emerald-400",
  },
  cyan: {
    bg: "bg-cyan-500/20",
    bgLight: "bg-cyan-500/10",
    border: "border-cyan-500/30",
    borderActive: "border-cyan-500/40",
    text: "text-cyan-300",
    textMuted: "text-cyan-400",
  },
  amber: {
    bg: "bg-amber-500/20",
    bgLight: "bg-amber-500/10",
    border: "border-amber-500/30",
    borderActive: "border-amber-500/40",
    text: "text-amber-300",
    textMuted: "text-amber-400",
  },
  violet: {
    bg: "bg-violet-500/20",
    bgLight: "bg-violet-500/10",
    border: "border-violet-500/30",
    borderActive: "border-violet-500/40",
    text: "text-violet-300",
    textMuted: "text-violet-400",
  },
  fuchsia: {
    bg: "bg-fuchsia-500/20",
    bgLight: "bg-fuchsia-500/10",
    border: "border-fuchsia-500/30",
    borderActive: "border-fuchsia-500/40",
    text: "text-fuchsia-300",
    textMuted: "text-fuchsia-400",
  },
};

function getColorClasses(colorKey: string): ColorClasses {
  return COLOR_CLASSES[colorKey] ?? COLOR_CLASSES["violet"]!;
}

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface AgentConfig {
  icon: React.ReactNode;
  colorKey: string;
  glowColor: string;
  label: string;
  description: string;
}

interface AgentWorkspacePanelProps {
  agent: AgentState;
  config: AgentConfig;
  streaming: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tool Operation Row
// ─────────────────────────────────────────────────────────────────────────────

const ToolOperationRow: React.FC<{
  tool: ToolState;
  colorKey: string;
  glowColor: string;
  streaming: boolean;
}> = ({ tool, colorKey, glowColor, streaming }) => {
  const isCalling = tool.phase === "calling";
  const isActive = streaming && isCalling;
  const isSuccess = tool.phase === "completed" && tool.ok !== false;
  const isFailed = tool.phase === "completed" && tool.ok === false;
  const colors = getColorClasses(colorKey);

  return (
    <motion.div
      initial={{ opacity: 0, x: -10, height: 0 }}
      animate={{ opacity: 1, x: 0, height: "auto" }}
      className={`
        rounded-lg border overflow-hidden
        ${isActive 
          ? `${colors.borderActive} bg-opacity-5` 
          : isSuccess
            ? "border-emerald-500/30 bg-emerald-500/5"
            : isFailed
              ? "border-rose-500/30 bg-rose-500/5"
              : "border-gray-700/30 bg-gray-800/20"
        }
      `}
      style={{
        boxShadow: isActive ? `0 0 15px ${glowColor}15` : undefined,
        backgroundColor: isActive ? `${glowColor}08` : undefined,
      }}
    >
      {/* Tool Row */}
      <div className="flex items-center gap-3 px-3 py-2.5">
        {/* Status Icon */}
        <div className={`
          h-6 w-6 rounded-md flex items-center justify-center flex-shrink-0
          ${isActive ? colors.bg : "bg-gray-700/30"}
        `}>
          {isActive ? (
            <Loader2 className={`h-3.5 w-3.5 ${colors.textMuted} animate-spin`} />
          ) : isSuccess ? (
            <Check className="h-3.5 w-3.5 text-emerald-400" />
          ) : isFailed ? (
            <X className="h-3.5 w-3.5 text-rose-400" />
          ) : (
            <Clock className="h-3.5 w-3.5 text-gray-500" />
          )}
        </div>

        {/* Tool Name */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono text-white/90 truncate">
              {tool.name}
            </span>
            {tool.server && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-700/50 text-gray-400 font-mono">
                {tool.server}
              </span>
            )}
          </div>
        </div>

        {/* Timing */}
        {tool.elapsedMs !== undefined && (
          <span className="text-xs text-gray-500 font-mono tabular-nums">
            {tool.elapsedMs.toFixed(0)}ms
          </span>
        )}
      </div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Streaming Text Display
// ─────────────────────────────────────────────────────────────────────────────

const StreamingText: React.FC<{
  text: string;
  streaming: boolean;
  colorKey: string;
  glowColor: string;
}> = ({ text, streaming, colorKey, glowColor }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const colors = getColorClasses(colorKey);

  // Auto-scroll to bottom as text streams
  useEffect(() => {
    if (containerRef.current && streaming) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [text, streaming]);

  if (!text) return null;

  return (
    <div
      ref={containerRef}
      className="relative rounded-xl border border-gray-700/40 bg-gray-900/60 overflow-hidden max-h-[300px] overflow-y-auto"
    >
      {/* Terminal header */}
      <div className="sticky top-0 flex items-center gap-2 px-3 py-2 bg-gray-900/90 border-b border-gray-700/40 backdrop-blur-sm">
        <Terminal className="h-3.5 w-3.5 text-gray-500" />
        <span className="text-xs text-gray-400 font-mono">Agent Output</span>
        {streaming && (
          <span className={`ml-auto flex items-center gap-1.5 text-xs ${colors.textMuted}`}>
            <Activity className="h-3 w-3 animate-pulse" />
            <span>streaming</span>
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <pre className="text-sm text-gray-200 whitespace-pre-wrap font-mono leading-relaxed">
          {text}
          {streaming && (
            <motion.span
              className="inline-block w-2 h-4 ml-0.5"
              style={{ backgroundColor: glowColor }}
              animate={{ opacity: [1, 0, 1] }}
              transition={{ duration: 0.8, repeat: Infinity }}
            />
          )}
        </pre>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Thinking Display
// ─────────────────────────────────────────────────────────────────────────────

const ThinkingDisplay: React.FC<{
  text: string;
  streaming: boolean;
  colorKey: string;
}> = ({ text, streaming, colorKey }) => {
  const [expanded, setExpanded] = React.useState(false);
  const colors = getColorClasses(colorKey);

  if (!text) return null;

  return (
    <div className="rounded-xl border border-gray-700/40 bg-gray-900/40 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-800/30"
      >
        <Cpu className={`h-3.5 w-3.5 ${colors.textMuted}`} />
        <span className="text-xs text-gray-400">Agent Reasoning</span>
        {streaming && (
          <span className={`text-xs ${colors.textMuted} animate-pulse`}>thinking...</span>
        )}
        <span className="ml-auto text-gray-600">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-gray-700/30"
          >
            <div className="p-3 max-h-48 overflow-y-auto">
              <p className="text-xs text-gray-400 leading-relaxed italic">
                {text}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const AgentWorkspacePanel: React.FC<AgentWorkspacePanelProps> = ({
  agent,
  config,
  streaming,
}) => {
  const isWorking = agent.status === "working";
  const isDone = agent.status === "done";
  const isError = agent.status === "error";
  const allTools = [...agent.completedTools, ...Array.from(agent.activeTools.values())];
  const colors = getColorClasses(config.colorKey);

  return (
    <div className="space-y-4">
      {/* Agent Header */}
      <div className="flex items-start gap-4">
        <div 
          className={`
            h-12 w-12 rounded-xl flex items-center justify-center
            ${colors.bg} ${colors.border}
          `}
          style={{
            boxShadow: isWorking ? `0 0 20px ${config.glowColor}30` : undefined,
          }}
        >
          <span className={colors.textMuted}>{config.icon}</span>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-white">{agent.displayName}</h3>
            <StatusBadge status={agent.status} colorKey={config.colorKey} />
          </div>
          <p className="text-sm text-gray-400 mt-0.5">{config.description}</p>
          {agent.error && (
            <p className="text-sm text-rose-400 mt-1">{agent.error}</p>
          )}
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 gap-3">
        <StatBox label="Operations" value={allTools.length.toString()} />
        <StatBox 
          label="Status" 
          value={agent.status.charAt(0).toUpperCase() + agent.status.slice(1)}
          highlight={isWorking}
          colorKey={config.colorKey}
        />
      </div>

      {/* Thinking Section */}
      {agent.thinkingText && (
        <ThinkingDisplay
          text={agent.thinkingText}
          streaming={streaming && isWorking}
          colorKey={config.colorKey}
        />
      )}

      {/* Tool Operations */}
      {allTools.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wide flex items-center gap-2">
            <Cpu className="h-3.5 w-3.5" />
            Operations ({allTools.length})
          </h4>
          <div className="space-y-2">
            {allTools.map((tool) => (
              <ToolOperationRow
                key={tool.id}
                tool={tool}
                colorKey={config.colorKey}
                glowColor={config.glowColor}
                streaming={streaming}
              />
            ))}
          </div>
        </div>
      )}

      {/* Streaming Output */}
      {(agent.tokenBuffer || agent.draftText) && (
        <StreamingText
          text={agent.tokenBuffer || agent.draftText}
          streaming={streaming && isWorking}
          colorKey={config.colorKey}
          glowColor={config.glowColor}
        />
      )}

      {/* Empty state */}
      {!isWorking && !isDone && !isError && allTools.length === 0 && !agent.tokenBuffer && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className={`h-16 w-16 rounded-2xl ${colors.bgLight} flex items-center justify-center mb-4`}>
            <span className={`${colors.textMuted} opacity-50`}>{config.icon}</span>
          </div>
          <p className="text-sm text-gray-500">Waiting to start...</p>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper Components
// ─────────────────────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: AgentState["status"]; colorKey: string }> = ({ status, colorKey }) => {
  const colors = getColorClasses(colorKey);
  
  const baseConfigs: Record<AgentState["status"], { bg: string; text: string; icon: React.ReactNode }> = {
    pending: { bg: "bg-gray-700/30", text: "text-gray-400", icon: <Clock className="h-3 w-3" /> },
    working: { bg: colors.bg, text: colors.textMuted, icon: <Loader2 className="h-3 w-3 animate-spin" /> },
    done: { bg: "bg-emerald-500/20", text: "text-emerald-400", icon: <Check className="h-3 w-3" /> },
    irrelevant: { bg: "bg-gray-700/30", text: "text-gray-500", icon: null },
    error: { bg: "bg-rose-500/20", text: "text-rose-400", icon: <X className="h-3 w-3" /> },
  };

  const c = baseConfigs[status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      {c.icon}
      {status}
    </span>
  );
};

const StatBox: React.FC<{ 
  label: string; 
  value: string; 
  highlight?: boolean;
  colorKey?: string;
}> = ({ label, value, highlight, colorKey }) => {
  const colors = colorKey ? getColorClasses(colorKey) : null;
  
  return (
    <div className={`
      rounded-lg px-3 py-2 border
      ${highlight && colors
        ? `${colors.bgLight} ${colors.border}`
        : "bg-gray-800/40 border-gray-700/30"
      }
    `}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-sm font-semibold ${highlight && colors ? colors.text : "text-white"}`}>
        {value}
      </div>
    </div>
  );
};

export default AgentWorkspacePanel;

